import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Check } from 'lucide-react';
import { ErrorCode, REFERRER_INTAKE_TITLE, type ReferrerIntakeView } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { PublicFrame } from '../../reservations/public/PublicFrame';
import type { FieldErrors } from '../public/form';
import { AssetsStep, ExpensesStep, IncomeStep, PersonStep } from '../public/steps';
import { submitReferrerIntake, useReferrerIntake } from './api';
import {
  buildSubmitBody,
  emptyIntakeForm,
  errorsByStep,
  validateStep,
  type IntakeForm,
  type IntakeStepNo,
  type ReferrerForm,
} from './form';
import { ReferrerStep } from './ReferrerStep';
import publicStyles from '../public/SelfDisclosurePublicPage.module.css';
import styles from './ReferrerIntakePage.module.css';

/**
 * `/t/:token` — „Selbstauskunft einreichen\" (D-248): a referrer partner submits a prospect's self-disclosure. The
 * frame carries the partner's logo and colour (fallback: the organization's, then DAMOKLES). Steps 1–4 are the
 * customer form's steps with the same validation, kept in local state (no autosave, no proofs, no signature);
 * step 5 „Tippgeber\" holds the referrer's own data and the two confirmations; one request submits everything.
 */
export function ReferrerIntakePage() {
  const { token = '' } = useParams();
  const query = useReferrerIntake(token);

  if (query.isPending) {
    return (
      <PublicFrame context={REFERRER_INTAKE_TITLE}>
        <div className={publicStyles.skeleton} aria-busy="true">
          <Skeleton width="40%" height={20} />
          <Skeleton width="65%" height={28} />
          <Skeleton height={320} />
        </div>
      </PublicFrame>
    );
  }

  if (query.isError) return <LinkErrorPage error={query.error} onRetry={() => void query.refetch()} />;

  return <IntakeFlow token={token} view={query.data} />;
}

function LinkErrorPage({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : null;
  let eyebrow = 'Fehler';
  let title = errorMessage(error);
  let text = 'Bitte versuchen Sie es in einem Moment erneut.';
  let retry = true;
  if (status === 410) {
    eyebrow = 'Link abgelaufen';
    title = 'Dieser Link ist abgelaufen.';
    text =
      'Bitte wenden Sie sich an Ihren Ansprechpartner bei der Organisation, die Ihnen den Link gegeben hat.';
    retry = false;
  } else if (status === 404 && code === ErrorCode.REFERRER_INTAKE_INACTIVE) {
    eyebrow = 'Link nicht aktiv';
    title = 'Dieser Link ist nicht mehr aktiv.';
    text =
      'Bitte wenden Sie sich an Ihren Ansprechpartner bei der Organisation, die Ihnen den Link gegeben hat.';
    retry = false;
  } else if (status === 404) {
    eyebrow = 'Link nicht gefunden';
    title = 'Dieser Link ist nicht gültig.';
    text = 'Bitte prüfen Sie, ob Sie den Link vollständig übernommen haben.';
    retry = false;
  } else if (status === 429) {
    title = 'Zu viele Anfragen.';
    text = 'Bitte versuchen Sie es in einigen Minuten erneut.';
  }
  return (
    <PublicFrame context={REFERRER_INTAKE_TITLE} width={620}>
      <div className={publicStyles.resultCard}>
        <Eyebrow as="div">{eyebrow}</Eyebrow>
        <h1 className={publicStyles.resultTitle}>{title}</h1>
        <p className={publicStyles.resultText}>{text}</p>
        {retry ? (
          <div className={publicStyles.buttonRow}>
            <Button variant="secondary" onClick={onRetry}>
              Erneut laden
            </Button>
          </div>
        ) : null}
      </div>
    </PublicFrame>
  );
}

const TITLES: Record<IntakeStepNo, { title: string; lead: string }> = {
  1: {
    title: 'Angaben zur Person',
    lead: 'Die Kontaktdaten des Interessenten. Eine E-Mail-Adresse oder Telefonnummer ist erforderlich.',
  },
  2: {
    title: 'Beschäftigung und Einkommen',
    lead: 'Daraus ergibt sich, welche Finanzierung für den Interessenten tragbar ist. Alle Beträge monatlich.',
  },
  3: {
    title: 'Monatliche Ausgaben',
    lead: 'Laufende Verpflichtungen des Interessenten, die nach dem Kauf weiterlaufen.',
  },
  4: { title: 'Vermögen', lead: 'Was der Interessent für den Kauf einsetzen kann.' },
  5: {
    title: 'Ihre Angaben als Tippgeber',
    lead: 'An Ihre E-Mail-Adresse geht die Bestätigung der Einreichung.',
  },
};

const STEP_FALLBACK_LABELS = ['Person', 'Einkommen', 'Ausgaben', 'Vermögen', 'Tippgeber'] as const;

function clampStep(value: number): IntakeStepNo {
  return Math.min(5, Math.max(1, Math.round(value))) as IntakeStepNo;
}

function countText(count: number): string {
  const words: Record<number, string> = {
    1: 'eine Angabe',
    2: 'zwei Angaben',
    3: 'drei Angaben',
    4: 'vier Angaben',
  };
  return words[count] ?? `${count} Angaben`;
}

interface Submitted {
  reference: string;
  email: string;
}

function IntakeFlow({ token, view }: { token: string; view: ReferrerIntakeView }) {
  const [form, setForm] = useState<IntakeForm>(() => emptyIntakeForm());
  const [step, setStep] = useState<IntakeStepNo>(1);
  const [reached, setReached] = useState<IntakeStepNo>(1);
  const [errors, setErrors] = useState<FieldErrors>({});
  /** Server field errors of steps other than the open one, shown when the step is opened. */
  const [pending, setPending] = useState<Map<IntakeStepNo, FieldErrors>>(new Map());
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<Submitted | null>(null);
  const [dirty, setDirty] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const organizationName = view.organization.name;
  const partnerLogo = view.partner.logoUrl;
  const frame = {
    context: `${REFERRER_INTAKE_TITLE} für ${organizationName}`,
    logoUrl: partnerLogo ?? view.organization.logoUrl,
    brandName: partnerLogo ? view.partner.name : organizationName,
    brandColor: view.partner.brandColor ?? view.organization.brandColor,
    organizationName,
    legal: { imprintUrl: view.organization.imprintUrl, privacyUrl: view.organization.privacyUrl },
  };

  // No autosave: warn before the tab closes with unsent entries.
  useEffect(() => {
    if (!dirty || submitted !== null) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, submitted]);

  function scrollTop() {
    window.setTimeout(() => topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0);
  }

  function goTo(next: IntakeStepNo, shown: FieldErrors = pending.get(next) ?? {}) {
    setStep(next);
    setReached((current) => (next > current ? next : current));
    setErrors(shown);
    setBanner(null);
    scrollTop();
  }

  function update<K extends keyof IntakeForm>(key: K, patch: Partial<IntakeForm[K]>) {
    setForm((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
    setDirty(true);
    setErrors((current) => {
      if (Object.keys(current).length === 0) return current;
      const next = { ...current };
      for (const field of Object.keys(patch)) delete next[field];
      return next;
    });
    setPending((current) => {
      const stepErrors = current.get(step);
      if (stepErrors === undefined) return current;
      const next = new Map(current);
      const remaining = { ...stepErrors };
      for (const field of Object.keys(patch)) delete remaining[field];
      if (Object.keys(remaining).length === 0) next.delete(step);
      else next.set(step, remaining);
      return next;
    });
  }

  /** Validates the open step; shows its errors and answers false when it is not complete. */
  function checkCurrent(): boolean {
    const found = validateStep(step, form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setBanner(null);
      scrollTop();
      return false;
    }
    return true;
  }

  function next() {
    if (step === 5 || !checkCurrent()) return;
    goTo(clampStep(step + 1));
  }

  function jump(target: IntakeStepNo) {
    if (target === step) return;
    if (target > step && !checkCurrent()) return;
    goTo(target);
  }

  async function submit() {
    // every step again: an earlier step may have been changed after it was left
    for (const number of [1, 2, 3, 4, 5] as const) {
      const found = validateStep(number, form);
      if (Object.keys(found).length > 0) {
        goTo(number, found);
        return;
      }
    }
    const body = buildSubmitBody(form);
    if (body === null) return;
    setSubmitting(true);
    setBanner(null);
    try {
      const result = await submitReferrerIntake(token, body);
      setSubmitted({ reference: result.reference, email: body.referrer.email });
      setPending(new Map());
      setErrors({});
      setDirty(false);
      scrollTop();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 429) {
        setBanner('Zu viele Einreichungen. Bitte versuchen Sie es in einigen Minuten erneut.');
      } else if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        const grouped = errorsByStep(cause.fields);
        const first = [...grouped.keys()].sort((a, b) => a - b)[0];
        if (first === undefined) {
          setBanner(Object.values(cause.fields)[0] ?? cause.message);
        } else {
          setPending(grouped);
          goTo(first, grouped.get(first) ?? {});
        }
      } else {
        setBanner(errorMessage(cause));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startOver() {
    const referrer: ReferrerForm = form.referrer;
    setForm(emptyIntakeForm({ name: referrer.name, email: referrer.email, phone: referrer.phone }));
    setSubmitted(null);
    setStep(1);
    setReached(1);
    setErrors({});
    setPending(new Map());
    setBanner(null);
    setDirty(false);
    scrollTop();
  }

  if (submitted !== null) {
    return (
      <PublicFrame {...frame} width={620}>
        <div ref={topRef} className={publicStyles.resultCard}>
          <div className={publicStyles.doneHead}>
            <span className={publicStyles.doneCheck} aria-hidden="true">
              <Check size={11} strokeWidth={2.5} />
            </span>
            <Eyebrow as="div">Eingereicht</Eyebrow>
          </div>
          <h1 className={publicStyles.resultTitle}>Vielen Dank.</h1>
          <p className={publicStyles.resultText}>
            Die Selbstauskunft für {submitted.reference} ist bei {organizationName} eingegangen. Sie erhalten
            eine Bestätigung per E-Mail an {submitted.email}.
          </p>
          <div className={publicStyles.buttonRow}>
            <Button size="large" onClick={startOver}>
              Weitere Selbstauskunft einreichen
            </Button>
          </div>
        </div>
      </PublicFrame>
    );
  }

  const labels = view.steps.length === 5 ? view.steps.map((entry) => entry.label) : [...STEP_FALLBACK_LABELS];
  const errorCount = Object.keys(errors).length;
  const pendingSteps = new Set(pending.keys());

  const sidebar = (
    <>
      <Eyebrow as="div">Schritt {step} von 5</Eyebrow>
      <ol className={publicStyles.stepper} aria-label="Schritte">
        {labels.map((label, index) => {
          const number = (index + 1) as IntakeStepNo;
          const stateOf = number === step ? 'current' : number < reached ? 'done' : 'open';
          const clickable = number !== step && number <= reached;
          const content = (
            <>
              <span
                className={clsx(publicStyles.stepCircle, publicStyles[`circle_${stateOf}`])}
                aria-hidden="true"
              >
                {stateOf === 'done' ? <Check size={11} strokeWidth={2.5} /> : number}
              </span>
              <span className={clsx(publicStyles.stepText, publicStyles[`text_${stateOf}`])}>
                {label}
                {pendingSteps.has(number) && number !== step ? (
                  <span className={styles.stepFlag}> · bitte prüfen</span>
                ) : null}
              </span>
            </>
          );
          return (
            <li
              key={label}
              className={publicStyles.stepItem}
              aria-current={stateOf === 'current' ? 'step' : undefined}
            >
              {clickable ? (
                <button
                  type="button"
                  className={clsx(publicStyles.stepRow, publicStyles.stepButton)}
                  onClick={() => jump(number)}
                >
                  {content}
                </button>
              ) : (
                <span
                  className={clsx(publicStyles.stepRow, stateOf === 'current' && publicStyles.stepRowCurrent)}
                >
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <div className={publicStyles.advisorCard}>
        <div className={publicStyles.advisorName}>{view.partner.name}</div>
        <div className={publicStyles.advisorRole}>Tippgeber-Partner von {organizationName}</div>
        <div className={publicStyles.advisorText}>
          Die Angaben werden erst mit „Selbstauskunft einreichen“ übermittelt. Bis dahin bleiben sie nur in
          diesem Fenster.
        </div>
      </div>
    </>
  );

  const { title, lead } = TITLES[step];
  const noop = () => undefined;

  return (
    <PublicFrame {...frame} sidebar={sidebar}>
      <div ref={topRef}>
        <h1 className={publicStyles.title}>{title}</h1>
        <p className={publicStyles.lead}>{lead}</p>
      </div>
      {errorCount > 0 ? (
        <div className={publicStyles.errorBanner} role="alert">
          <span className={publicStyles.errorBannerTitle}>Bitte prüfen Sie {countText(errorCount)}.</span>
          <span className={publicStyles.errorBannerText}>Ihre bisherigen Eingaben bleiben erhalten.</span>
        </div>
      ) : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}

      {step === 5 ? (
        <ReferrerStep
          form={form.referrer}
          errors={errors}
          organizationName={organizationName}
          onChange={(patch) => update('referrer', patch)}
        />
      ) : (
        <div className={publicStyles.card}>
          {step === 1 ? (
            <PersonStep
              form={form.person}
              errors={errors}
              onChange={(patch) => update('person', patch)}
              onBlur={noop}
            />
          ) : step === 2 ? (
            <IncomeStep
              form={form.income}
              errors={errors}
              onChange={(patch) => update('income', patch)}
              onBlur={noop}
            />
          ) : step === 3 ? (
            <ExpensesStep
              form={form.expenses}
              errors={errors}
              onChange={(patch) => update('expenses', patch)}
              onBlur={noop}
            />
          ) : (
            <AssetsStep
              form={form.assets}
              errors={errors}
              onChange={(patch) => update('assets', patch)}
              onBlur={noop}
            />
          )}
        </div>
      )}

      <div className={publicStyles.footerRow}>
        {step > 1 ? (
          <Button variant="secondary" onClick={() => jump(clampStep(step - 1))} disabled={submitting}>
            Zurück
          </Button>
        ) : null}
        <span className={publicStyles.footerNote} />
        {step === 5 ? (
          <Button size="large" loading={submitting} onClick={() => void submit()}>
            Selbstauskunft einreichen
          </Button>
        ) : (
          <Button size="large" onClick={next}>
            Weiter
          </Button>
        )}
      </div>
    </PublicFrame>
  );
}
